/**
 * An ⓘ that sits ABOVE and AFTER what it annotates, like a footnote marker.
 *
 * Centred on the baseline it competed with its own subject: beside a pair of
 * buttons it read as a third button, and inline in a sentence it read as
 * punctuation. Raised and trailing, it reads as a reference to the thing on its
 * left — which is exactly what it is.
 *
 * The note has TWO surfaces and both are required. `/pr-review` iter 1 on the
 * cl.4 work (Agent A 3(c)+3(d), Agent C M4) rejected a hover-only version for
 * three reasons: a tooltip wrapper adds a tab stop before the primary action;
 * it carries `aria-describedby` on its own span, so a screen reader focused on
 * the BUTTON never hears the note; and hover-only is out of reach for keyboard
 * and touch. So the caller keeps an `aria-describedby` pointing at a `sr-only`
 * copy of the text, and this is the sighted-reader surface — itself a real
 * `button`, so it is focusable and openable from the keyboard.
 */
import { Tooltip } from "@nu-assist/ui";

export function InfoHint({
  content,
  label = "More information",
  escapesOverflow = false,
  centered = false,
}: {
  content: React.ReactNode;
  /** Accessible name. Override when a page has more than one hint. */
  label?: string;
  /** Pass on a hint rendered inside a SCROLL CONTAINER — the contacts table's
   *  rows, in practice. Without it the bubble is `absolute` and the container's
   *  `overflow-auto` cuts it off at the edge; the last visible rows lose most
   *  of the note (operator, 2026-09-08). See `Tooltip.escapesOverflow` for what
   *  it does and what it costs. Off by default, so a hint outside a scroll box
   *  pays nothing. */
  escapesOverflow?: boolean;
  /** Sit on the row's centre line instead of raised like a superscript —
   *  for a hint beside a control (an input) rather than beside text. Off by
   *  default, so every existing hint keeps its raised look. */
  centered?: boolean;
}) {
  return (
    <Tooltip
      side="bottom"
      align="end"
      escapesOverflow={escapesOverflow}
      // CLICK PINS IT, always — an ⓘ is the one control everybody clicks, and
      // until 2026-09-08 clicking this one did nothing at all: it was a
      // `<button>` with no handler, revealed by hover alone. That made the
      // notes unreachable on touch and awkward on a desktop, where the bubble
      // is `pointer-events-none` behind a gap, so moving toward a long note to
      // read it dismissed the note. Hover still previews.
      clickToPin
      maxWidthClassName="max-w-[260px]"
      // `self-start` lifts it to the top of a flex row; `-translate-y-1` takes
      // it that last bit above the line so it reads as raised rather than
      // merely top-aligned. Both are needed — `self-start` alone still sits ON
      // the first line of a tall neighbour.
      // `self-start` ONLY. The `-translate-y-1` that used to ride here moved
      // to the button below, and that is not a refactor — it is THE BUG.
      //
      // This class lands on `Tooltip`'s wrap element, the bubble's direct
      // ancestor. A `transform` on an ancestor makes that element the
      // CONTAINING BLOCK for any `position: fixed` descendant (CSS Transforms
      // §3), so the `escapesOverflow` bubble was being positioned against a
      // 14x14px icon instead of against the viewport. Its measured viewport
      // coordinates were then interpreted as offsets inside that icon, which
      // is why the note appeared far from its trigger, or off screen entirely,
      // and why "los info icon no funcionan" survived three separate fixes to
      // the positioning code — every one of which was correct about its own
      // symptom and none of which could reach this.
      //
      // Invisible to the jsdom tests by construction: jsdom computes no layout,
      // so `fixed` never resolves against anything and the containing block
      // cannot exist to be wrong. Found by reading, in the iter-4 third-party
      // review (C1).
      className={centered ? "self-center" : "self-start"}
      content={<span className="text-left">{content}</span>}
    >
      <button
        type="button"
        data-testid="info-hint"
        aria-label={label}
        // `-translate-y-1` lives HERE now: it is the icon that should read as
        // raised, and a transform on the button creates a containing block for
        // nothing (the bubble is not inside it).
        className={`inline-flex h-3.5 w-3.5 ${centered ? "" : "-translate-y-1"} items-center justify-center rounded-pill border border-border text-[9px] font-semibold leading-none text-text-muted hover:border-accent hover:text-accent focus:outline-none focus:ring-1 focus:ring-accent`}
      >
        i
      </button>
    </Tooltip>
  );
}
